/**
 * GA4 event payload builders for Native Interactive Site Map (ISM) tracking.
 *
 * Pure functions — no Redux, no DOM, no dispatch. Each returns a `{ eventName,
 * customProperties }` shape ready to spread into `googleAnalytics4Event(...)`. Three of the four
 * events share GA4's generic `native_map_events` name, differentiated by `click_target`/
 * `click_target1`/`click_target2` — the same convention
 * `pages/searchResults/components/eventTracking/googleAnalytics4EventsHandler.js` uses
 * elsewhere in this app. `buildLotSelectedEvent` is the one exception in the source table — it
 * gets its own dedicated event name, not the generic one.
 *
 * @module nativeInteractiveSiteMap/services/googleAnalytics4EventBuilder
 */

/**
 * @param {object} params
 * @param {string} params.interactionType - 'zoom_in' | 'zoom_out' | 'pan' | 'map_type_changed'
 * @param {string} params.inputMethod - 'mouse' | 'touch' | 'button'
 * @param {string} [params.mapType] - 'road' | 'satellite' — only set for map_type_changed
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildMapInteractionEvent({ interactionType, inputMethod, mapType }) {
  const customProperties = {
    click_target: interactionType,
    click_target1: inputMethod,
  };

  if (mapType) {
    customProperties.click_target2 = `map_type_${mapType}`;
  }

  return { eventName: 'native_map_events', customProperties };
}

/**
 * @param {object} params
 * @param {string|null} params.selectedChip - pill path only
 * @param {string|null} params.previousChip - pill path only
 * @param {number} params.resultsCount - lot count for the current view
 * @param {number} params.cardCount - number of cards rendered in the active tab
 * @param {'lot_status_type'|'lot_selection'} params.source - what updated the panel
 * @param {boolean} params.isMobile
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildPanelUpdatedEvent({ selectedChip, previousChip, resultsCount, cardCount, source, isMobile }) {
  const customProperties = {
    click_target2: resultsCount,
    click_target3: cardCount,
    filter_name: `panel_type_${isMobile ? 'sheet' : 'panel'}`,
  };

  if (source === 'lot_selection') {
    customProperties.click_target = 'panel_updated';
    customProperties.search_value = 'lot_selection';
    customProperties.position = 'map_lot';
  } else {
    customProperties.click_target = 'lot_status_type_selected';
    customProperties.search_value = selectedChip;
    customProperties.click_target1 = `previouslot_${previousChip}`;
    customProperties.position = 'map_pills';
  }

  return { eventName: 'native_map_events', customProperties };
}

/**
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildPanelScrollEvent() {
  return {
    eventName: 'native_map_events',
    customProperties: { click_target: 'lot_status_type_scrolled' },
  };
}

/**
 * Mobile drawer interaction — a vertical drag or the drawer being opened. Reuses the generic
 * `native_map_events` name with its own `click_target`, like the other non-lot events above.
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildDrawerDraggedEvent() {
  return {
    eventName: 'native_map_events',
    customProperties: { click_target: 'map_sheet_dragged' },
  };
}

/**
 * Encodes the lot attributes into `click_target1`'s `key:value;key:value` format, skipping any
 * attribute that's missing — the lot map inventory API can return `null` or `''` for lotType/
 * lotSize/lotFacing (see services/processLotMapInventory.js), and a trailing `f:` with nothing
 * after it is worse than just leaving that attribute out.
 * @param {{ plansCount: number, lotType: string|null, lotSize: number|null, lotFacing: string|null }} params
 * @returns {string}
 */
function encodeLotAttributes({ plansCount, lotType, lotSize, lotFacing }) {
  return [
    ['c', plansCount],
    ['t', lotType],
    ['s', lotSize],
    ['f', lotFacing],
  ]
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => `${key}:${value}`)
    .join(';');
}

/**
 * @param {object} params
 * @param {object} params.lot - raw store lot object (see services/processLotMapInventory.js)
 * @param {number|null} params.planId
 * @param {number|null} params.specId
 * @param {number} params.plansCount
 * @param {boolean} params.isMobile
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildLotSelectedEvent({ lot, planId, specId, plansCount, isMobile }) {
  return {
    eventName: isMobile ? 'native_map_lot_selected_m' : 'native_map_lot_selected_d',
    customProperties: {
      plan_id: planId,
      spec_id: specId,
      lot_id: lot.lotId,
      lot_number: lot.displayLabel,
      lot_group: lot.lotGroupName,
      lot_status: lot.status,
      click_target1: encodeLotAttributes({
        plansCount,
        lotType: lot.lotType,
        lotSize: lot.lotSize,
        lotFacing: lot.lotFacing,
      }),
    },
  };
}
